import { useState, useEffect } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { TipShareDistributionConfig, TipShareContributionConfig } from '../../types';
import { TipShareFormData, DistributionType, AutoDistributionMethod } from '../types';

const RESET_OPTIONS = {
  keepDirtyValues: false,
  keepErrors: false,
  keepDirty: false,
  keepIsSubmitted: false,
  keepTouched: false,
  keepIsValid: false,
  keepSubmitCount: false,
} as const;

interface UseTipShareFormSyncParams {
  reset: UseFormReset<TipShareFormData>;
  setValue: UseFormSetValue<TipShareFormData>;
  distributionType: DistributionType | '';
  distributionMethod: AutoDistributionMethod | '';
  isDistributionLoading: boolean;
  isContributionLoading: boolean;
  distributionConfig: TipShareDistributionConfig | null | undefined;
  contributionConfig: TipShareContributionConfig | null | undefined;
}

interface UseTipShareFormSyncReturn {
  isInitialLoadComplete: boolean;
}

/**
 * Manages all form synchronisation side-effects for TipShareSettingsPage:
 * - Tracks when the initial API load has finished.
 * - Auto-selects "By Job" distribution method when Automatic is chosen.
 * - Auto-manages the contribution toggle based on the chosen distribution type.
 * - Populates the form once API data arrives.
 */
export default function useTipShareFormSync({
  reset,
  setValue,
  distributionType,
  distributionMethod,
  isDistributionLoading,
  isContributionLoading,
  distributionConfig,
  contributionConfig,
}: UseTipShareFormSyncParams): UseTipShareFormSyncReturn {
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  // Track when both API calls have completed for the first time
  useEffect(() => {
    if (!isDistributionLoading && !isContributionLoading && !isInitialLoadComplete) {
      setIsInitialLoadComplete(true);
    }
  }, [isDistributionLoading, isContributionLoading, isInitialLoadComplete]);

  // Auto-select "By Job" when user selects "Automatic"
  useEffect(() => {
    if (distributionType === DistributionType.AUTOMATIC && !distributionMethod) {
      setValue('distributionMethod', AutoDistributionMethod.BY_JOB);
    }
  }, [distributionType, distributionMethod, setValue]);

  // Auto-manage contribution toggle based on distribution type.
  // Only runs after initial load is complete to avoid overwriting loaded data.
  // Automatic → Contribution ON; Manual → Contribution OFF.
  useEffect(() => {
    if (!isInitialLoadComplete) return;

    if (distributionType === DistributionType.AUTOMATIC) {
      setValue('enableTipShareContribution', true, { shouldValidate: false });
    } else if (distributionType === DistributionType.MANUAL) {
      setValue('enableTipShareContribution', false, { shouldValidate: false });
    }
  }, [distributionType, setValue, isInitialLoadComplete]);

  // Populate form with fetched data using reset() for better performance
  useEffect(() => {
    if (distributionConfig || contributionConfig) {
      const isDistributionActive = distributionConfig?.isActive === true;

      const distributionFields: Partial<TipShareFormData> = {
        enableTipShareDistribution: isDistributionActive,
        distributionType: (distributionConfig?.distributionType || '') as DistributionType | '',
        distributionMethod: (distributionConfig?.distributionMethod || '') as AutoDistributionMethod | '',
        recalculateOnPosImport: distributionConfig?.recalculateOnPosImport ?? false,
        autoBalanceGridDistribution: distributionConfig?.autoBalanceGridDistribution ?? false,
      };

      const contributionFields: Partial<TipShareFormData> = {
        enableTipShareContribution: contributionConfig?.isActive === true,
        contributionPercentage: contributionConfig?.contributionPercentage ?? 0,
        includeComps: contributionConfig?.includeComps ?? true,
        includePromotions: contributionConfig?.includePromotions ?? true,
        includeTax: contributionConfig?.includeTax ?? true,
      };

      reset({ ...distributionFields, ...contributionFields }, RESET_OPTIONS);
    }
  }, [distributionConfig, contributionConfig, reset]);

  return { isInitialLoadComplete };
}
